import { fmtSeconds } from './utils';

/**
 * Everything a "Weiterlernen" card needs to build its link.
 *
 * Home asks "most recently touched lesson across all courses" and the course
 * page asks "first incomplete lesson of this course" — two questions, but the
 * answers land the learner in the same place, so they share one builder rather
 * than two ad-hoc template strings that can drift apart.
 */
export interface ResumeTarget {
  courseSlug: string;
  moduleId: string;
  lessonId: string;
  lastPositionSeconds: number;
}

/**
 * Straight at the lesson, never at the module page in between.
 *
 * Carries the stored position as `?t=`, which the Lesson page already reads. A
 * lesson that was never opened gets a bare URL rather than a pointless `?t=0`.
 */
export function resumeHref(target: ResumeTarget): string {
  const base = `/kurse/${target.courseSlug}/${target.moduleId}/${target.lessonId}`;
  const at = Math.floor(target.lastPositionSeconds);
  return at > 0 ? `${base}?t=${at}` : base;
}

/** How far into a lesson the learner is, as a bar percentage. */
export function watchedPct(lastPositionSeconds: number, durationSeconds: number): number {
  if (durationSeconds <= 0) return 0;
  return Math.min(100, Math.round((lastPositionSeconds / durationSeconds) * 100));
}

/**
 * The card's one line of context: where the lesson sits, then how far in the
 * learner is. `context` is the course on Home and the module on a course page —
 * the course being the page they're already on there.
 *
 * A lesson never opened shows its length instead of "0:00 / 12:30", which reads
 * as broken progress rather than as an invitation.
 */
export function resumeSubtitle(
  context: string,
  lastPositionSeconds: number,
  durationSeconds: number,
): string {
  if (durationSeconds <= 0) return context;
  if (lastPositionSeconds <= 0) return `${context} · ${fmtSeconds(durationSeconds)}`;
  return `${context} · ${fmtSeconds(lastPositionSeconds)} / ${fmtSeconds(durationSeconds)}`;
}
